import React, { createContext, useContext, useMemo } from 'react';

import { CollectionContext } from './CollectionContext';
import { normalizeId } from '../utils/ids';

/**
 * WHICH COLLECTION A SURFACE IS IN — the one input the qualified badge's rule
 * reads (docs/platform-standards.md §1, "Qualify outside your collection";
 * utils/recordQualifier.js).
 *
 * A card or an item shown anywhere OUTSIDE the collection that owns it wears
 * its collection in its badge; inside its own collection it does not. So a
 * badge needs to know where it is being drawn, and this answers it:
 *
 *   - Under a collection's routes (`CollectionsLayout`: the items list, the
 *     home, an item's page and pane, settings, the multi add and notification
 *     editors) the answer is that collection — read from `CollectionContext`,
 *     which the layout already mounts, so no route has to say it twice.
 *   - Everywhere else (Home, the palette, History, the inbox, Drafts, Discover,
 *     the card library, admin) there is no collection: `collectionId` is null,
 *     and every record whose collection is known is qualified.
 *   - `<CollectionScope collectionId={id}>` names one explicitly — a surface
 *     outside the layout that is nonetheless about one collection (Drafts
 *     narrowed to one, a card's own page).
 *   - `<CollectionScope qualify={false}>` turns qualification off beneath it:
 *     the PUBLIC pages (a share link's view, an intake form), which disclose
 *     only what the link discloses — never a collection's identity
 *     (keepr-api `buildLookupTitles`) — and paper (the print sheet). A nested
 *     scope can never turn it back on.
 *
 *   useCollectionScope() → { collectionId: string | null, qualify: boolean }
 */
const CollectionScopeContext = createContext(null);

const UNSCOPED = Object.freeze({ collectionId: null, qualify: true });

export function CollectionScope({ collectionId, qualify = true, children }) {
	const parent = useContext(CollectionScopeContext);
	const layout = useContext(CollectionContext);
	const inherited = parent || {
		collectionId: normalizeId(layout?.resolvedCollectionId || layout?.collection?._id) || null,
		qualify: true,
	};
	const id = collectionId === undefined ? inherited.collectionId : (normalizeId(collectionId) || null);
	const allowed = inherited.qualify !== false && qualify !== false;
	const value = useMemo(() => ({ collectionId: id, qualify: allowed }), [id, allowed]);
	return <CollectionScopeContext.Provider value={value}>{children}</CollectionScopeContext.Provider>;
}

export function useCollectionScope() {
	const explicit = useContext(CollectionScopeContext);
	const layout = useContext(CollectionContext);
	const layoutId = normalizeId(layout?.resolvedCollectionId || layout?.collection?._id) || null;
	return useMemo(() => {
		if (explicit) return explicit;
		return layoutId ? { collectionId: layoutId, qualify: true } : UNSCOPED;
	}, [explicit, layoutId]);
}

export default CollectionScopeContext;
